import { useState, useEffect } from 'react'
import { Loader2, FolderOpen, ImageIcon } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { api } from '@/api/client'

interface MediaFolder {
  id: string
  name: string
  images: { id: string; url: string }[]
}

interface FolderPickerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Recebe o id e o nome da pasta escolhida. O chamador seta a imagem como `__random__:<id>`. */
  onSelect: (folderId: string, folderName: string) => void
}

/** Modal pra escolher uma PASTA da biblioteca (não uma imagem). A etapa/card passa a
 *  sortear uma foto aleatória dessa pasta a cada envio (valor `__random__:<folderId>`). */
export function FolderPickerModal({ open, onOpenChange, onSelect }: FolderPickerModalProps) {
  const [folders, setFolders] = useState<MediaFolder[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open) {
      setLoading(true)
      api.get<MediaFolder[]>('/media-folders')
        .then((data) => setFolders(data || []))
        .catch(() => {})
        .finally(() => setLoading(false))
    }
  }, [open])

  const imgCount = (f: MediaFolder) => f.images.filter((i) => !i.url.match(/\.(mp4|mov|webm)$/i)).length

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] flex flex-col gap-0 p-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border shrink-0">
          <DialogTitle className="text-sm">Atribuir pasta de mídias</DialogTitle>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            A cada envio, o sistema sorteia uma foto aleatória da pasta escolhida — a imagem varia sozinha.
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 min-h-[200px]">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : folders.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32">
              <ImageIcon className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Nenhuma pasta na biblioteca</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {folders.map((f) => {
                const n = imgCount(f)
                const thumb = f.images.find((i) => !i.url.match(/\.(mp4|mov|webm)$/i))
                return (
                  <button
                    key={f.id}
                    onClick={() => { onSelect(f.id, f.name); onOpenChange(false) }}
                    disabled={n === 0}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-border hover:border-primary hover:bg-muted/40 transition-all text-left disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {thumb ? (
                      <img src={thumb.url} alt="" className="h-10 w-10 rounded-md object-cover shrink-0" loading="lazy" />
                    ) : (
                      <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center shrink-0">
                        <FolderOpen className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{f.name}</div>
                      <div className="text-[11px] text-muted-foreground">{n} foto{n === 1 ? '' : 's'}</div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
